import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Shield, Smartphone, Lock } from "lucide-react";
import { signOut } from "firebase/auth";
import SettingsLayout from "./SettingsLayout";
import useSettingsProfile from "./useSettingsProfile";
import { auth } from "../../firebase";
import { clearStoredAuthSession } from "../../utils/authSession";
import { invalidateAuthCache } from "../../hooks/useAuthAccess";

export default function SecuritySettings() {
  const navigate = useNavigate();
  const { user, profile, loading } = useSettingsProfile();

  const logout = async () => {
    try {
      invalidateAuthCache();
      await signOut(auth);
      clearStoredAuthSession();
      navigate("/login");
    } catch (err) {
      console.error(err);
    }
  };

  const phone = profile?.mobileNumber || profile?.phone || user?.phoneNumber || "—";

  return (
    <SettingsLayout
      title="Security"
      description="Karanki uses phone OTP login. Keep your number private and log out on shared devices."
    >
      {loading ? (
        <div className="card flex justify-center p-10">
          <Loader2 className="animate-spin text-brand-600" size={28} />
        </div>
      ) : (
        <>
          <div className="card p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-soft">
                <Shield size={22} />
              </div>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-800">Sign-in method</h2>
                <p className="mt-1 text-[13.5px] leading-6 text-ink-500">
                  You sign in with OTP sent to your mobile number. Password changes are not used for member accounts.
                </p>
                <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-sand-100 px-3 py-1 text-[12px] font-semibold text-ink-700">
                  <Smartphone size={12} /> Registered: {phone}
                </p>
              </div>
            </div>
          </div>

          <div className="card mt-4 p-5 sm:p-6">
            <p className="section-subtle">Best practices</p>
            <ul className="mt-3 space-y-3 text-[13.5px] leading-6 text-ink-500">
              <li className="flex gap-3"><Lock size={15} className="mt-1 shrink-0 text-brand-600" /> Do not share OTP codes with anyone, including people claiming to be from Karanki.</li>
              <li className="flex gap-3"><LogOut size={15} className="mt-1 shrink-0 text-brand-600" /> Log out when using a public or family computer.</li>
              <li className="flex gap-3"><Shield size={15} className="mt-1 shrink-0 text-brand-600" /> Report suspicious messages from other profiles via Help &amp; Support.</li>
            </ul>
          </div>

          <button
            type="button"
            onClick={logout}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-white px-4 py-3.5 text-[13.5px] font-semibold text-rose-600 transition hover:bg-rose-50"
          >
            <LogOut size={16} /> Log out on this device
          </button>
        </>
      )}
    </SettingsLayout>
  );
}
